import { OverflowList } from "@theme/critical";
import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import {
  debounce,
  isDesktopBreakpoint,
  mediaQueryLarge,
  requestYieldCallback,
} from "@theme/utilities";
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
  SlideshowSelectEvent,
} from "@theme/events";
import { morph } from "@theme/morph";

/**
 * @typedef {object} VariantBadge
 * @property {string} text - The badge text to display
 */

/**
 * @typedef {object} VariantBadgeSettings
 * @property {number} max_badges - Maximum number of badges to display
 * @property {string} position - Badge position (e.g., "top-left", "top-right")
 * @property {string} sale_color_scheme - Color scheme for variant badges
 */

/**
 * @typedef {object} VariantBadgeData
 * @property {VariantBadgeSettings} settings - Badge display settings
 * @property {Object.<string, VariantBadge[]>} variants - Map of variant IDs to their badges
 */

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ["productCardLink"];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Parses variant badge data from the JSON script tag
   * @returns {VariantBadgeData|null} Parsed badge data or null if not found
   */
  #parseVariantBadgeData() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const dataElement = this.querySelector(`#variant-badges-data-${productId}`);
    if (!dataElement) return null;

    try {
      return JSON.parse(dataElement.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Gets the badge root element for the selected variant slideshow.
   * @param {string} variantId - The active variant ID
   * @returns {HTMLElement | null}
   */
  #getBadgeRootForVariant(variantId) {
    const { cardGallery } = this.refs;
    if (cardGallery instanceof HTMLElement && variantId) {
      const variantSlideshow =
        cardGallery.querySelector(
          `slideshow-component[data-variant-slideshow-id="${variantId}"]`,
        ) ??
        cardGallery.querySelector(
          "slideshow-component:has(slideshow-slide[data-variant-id='" +
            variantId +
            "'])",
        );
      const variantBadgeRoot =
        variantSlideshow?.querySelector(".product-badges");
      if (variantBadgeRoot instanceof HTMLElement) {
        return variantBadgeRoot;
      }
    }

    const fallbackBadgeRoot = this.querySelector(".product-badges");
    return fallbackBadgeRoot instanceof HTMLElement ? fallbackBadgeRoot : null;
  }

  /**
   * Generates HTML for a single badge
   * @param {string} badgeText - The text to display in the badge
   * @param {string} colorScheme - The color scheme class to apply
   * @returns {HTMLElement} Badge element
   */
  #getBadgeHTML(badgeText, colorScheme, dataAttribute = "data-variant-badge") {
    const div = document.createElement("div");
    div.className = `product-badges__badge product-badges__badge--rectangle color-${colorScheme}`;
    div.setAttribute(dataAttribute, "");
    div.textContent = badgeText;
    return div;
  }

  /**
   * Clears all variant badges from the badge container
   */
  #clearVariantBadges() {
    const variantBadges = this.querySelectorAll(
      "[data-variant-badge], [data-sold-out-badge]",
    );
    variantBadges.forEach((badge) => badge.remove());
  }

  /**
   * Gets the variant input for a given variant ID
   * @param {string} variantId - Variant ID to find
   * @returns {HTMLInputElement|null}
   */
  #getVariantInputById(variantId) {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[data-variant-id="${variantId}"]`)
    );
  }

  /**
   * Renders variant badges for the specified variant ID
   * @param {string} variantId - The variant ID to render badges for
   */
  #renderVariantBadges(variantId) {
    if (!variantId) {
      this.#clearVariantBadges();
      return;
    }

    const badgeData = this.#parseVariantBadgeData();
    if (!badgeData) return;

    const settings = badgeData.settings || {};
    const variants = badgeData.variants || {};
    const variantBadges = variants[variantId];
    const variantInput = this.#getVariantInputById(variantId);
    const isVariantSoldOut = variantInput?.dataset.optionAvailable === "false";

    this.#clearVariantBadges();

    const badgesRoot = this.#getBadgeRootForVariant(variantId);
    if (!badgesRoot) return;

    const productBadges = Array.from(
      badgesRoot.querySelectorAll("[data-product-badge]"),
    );

    productBadges.forEach((badge) => {
      if (badge.hasAttribute("data-hidden-for-sold-out")) {
        badge.removeAttribute("hidden");
        badge.removeAttribute("data-hidden-for-sold-out");
      }
    });

    const maxBadgesSetting = Number(settings.max_badges);
    const maxBadges =
      Number.isFinite(maxBadgesSetting) && maxBadgesSetting >= 0
        ? maxBadgesSetting
        : 4;

    let badgeContainer = badgesRoot.querySelector(":scope > .flex");
    if (!badgeContainer) {
      badgeContainer = document.createElement("div");
      badgeContainer.className = "flex flex-col gap-[5px]";
      badgeContainer.style.display = "flex";
      badgeContainer.style.flexDirection = "column";
      badgeContainer.style.gap = "5px";
      badgesRoot.appendChild(badgeContainer);
    }

    if (!badgeContainer) return;

    const hasStaticSoldOutBadge = Boolean(
      badgesRoot.querySelector("[data-static-sold-out-badge]"),
    );

    const shouldRenderDynamicSoldOut =
      isVariantSoldOut && !hasStaticSoldOutBadge && maxBadges > 0;

    if (shouldRenderDynamicSoldOut) {
      const soldOutBadge = this.#getBadgeHTML(
        "Sold out",
        settings.sale_color_scheme || "scheme-1",
        "data-sold-out-badge",
      );
      badgeContainer.prepend(soldOutBadge);

      const allowedProductBadges = Math.max(0, maxBadges - 1);
      if (productBadges.length > allowedProductBadges) {
        for (let i = productBadges.length - 1; i >= allowedProductBadges; i--) {
          const badge = productBadges[i];
          badge.setAttribute("hidden", "");
          badge.setAttribute("data-hidden-for-sold-out", "");
        }
      }
    }

    const visibleProductBadgeCount = badgesRoot.querySelectorAll(
      "[data-product-badge]:not([hidden])",
    ).length;
    const occupiedSlots =
      visibleProductBadgeCount + (shouldRenderDynamicSoldOut ? 1 : 0);
    const remainingSlots = Math.max(0, maxBadges - occupiedSlots);

    if (remainingSlots <= 0) return;
    if (!variantBadges || !Array.isArray(variantBadges)) return;

    const badgesToRender = variantBadges.slice(0, remainingSlots);
    const colorScheme = settings.sale_color_scheme || "scheme-1";

    badgesToRender.forEach((badge) => {
      if (badge.text) {
        const badgeElement = this.#getBadgeHTML(badge.text, colorScheme);
        badgeContainer.appendChild(badgeElement);
      }
    });
  }

  /**
   * Initializes variant badges on component mount
   */
  #initializeVariantBadges() {
    const selectedVariantId = this.getSelectedVariantId();
    if (selectedVariantId) {
      this.#renderVariantBadges(selectedVariantId);
    }
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, "_blank");
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement))
      throw new Error("Product card link not found");
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
    );
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect,
    );
    mediaQueryLarge.addEventListener("change", this.#handleQuickAdd);

    this.addEventListener("click", this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    this.#initializeVariantBadges();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide =
      this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute("loading");
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener("pointerenter", this.#fetchProductPageHandler);
    this.removeEventListener("focusin", this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener("pointerenter", this.#fetchProductPageHandler);
      this.addEventListener("focusin", this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.updateSlideshow(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    //this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute("data-no-swatch-selected");

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();

    // Update variant badges for the selected variant
    const variantId = event.detail.resource?.id;
    if (variantId) {
      this.#renderVariantBadges(String(variantId));
    }

    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector(
      "swatches-variant-picker-component overflow-list",
    );

    const isActiveOverflowList = overflowList?.querySelector(
      '[slot="overflow"]',
    )
      ? true
      : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent("reflow", {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(
      `product-price [ref='priceContainer']`,
    )[1];
    const newPriceElement = event.detail.data.html.querySelector(
      `product-price [ref='priceContainer']`,
    );

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the DOM with a new slideshow.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSlideshow(event) {
    const variantId = event.detail.resource?.id;
    const { slideshow } = this.refs;
    if (!slideshow || !variantId) return;

    const { scroller, slideshowControls } = slideshow.refs;
    if (!scroller) return;

    // Find the template with data-variant-id
    const template = this.querySelector(
      `template[data-variant-id="${variantId}"]`,
    );
    if (!template) return;

    // Empty the scroller
    scroller.innerHTML = "";

    // Copy HTML from the template into the scroller
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = template.innerHTML;
    let timeline = "--slideshow-timeline: ";
    let dots = "";
    const slides = tempDiv.querySelectorAll('[ref="slides[]"]');
    Array.from(slides).forEach((node, index) => {
      if (index === 0) {
        node.setAttribute("aria-hidden", "false");
      }
      scroller.appendChild(node);
      timeline += " --slide-" + (index + 1);
      const selected = index === 0;
      dots += `<li><button class="slideshow-control button button-unstyled" style="animation-timeline: --slide-${index + 1}" aria-label="Slide ${index + 1} of ${slides.length}" on:click="/select/${index}" ref="dots[]" aria-selected="${selected}">${index + 1}</button></li>`;
    });

    slideshow.setAttribute("data-slides-length", slides.length);
    slideshow.style = timeline;

    if (!slideshowControls) return;
    slideshowControls.querySelector(".slideshow-controls__dots").innerHTML =
      dots;

    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement =
      event.detail.data.html?.querySelector("product-card a");
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector("product-card-link")
      ?.getAttribute("data-featured-media-url");

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest("product-card-link"))
      this.closest("product-card-link")?.setAttribute(
        "data-featured-media-url",
        featuredMediaUrl,
      );

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll("input:checked")
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === "true");
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector(".add-to-cart__button button");

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow, cardGallery } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId =
        this.variantPicker?.selectedOption.dataset.optionMediaId,
      selectedVariantId = this.variantPicker?.selectedOption.dataset.variantId;

    if (!slideshow) return;

    // Initialize the slideshow if it was deferred (hidden on initial page load)
    slideshow.initIfNeeded?.();

    const { slides = [] } = slideshow.refs;

    if (cardGallery && selectedVariantId) {
      cardGallery.setAttribute("data-selected-variant-id", selectedVariantId);
    }

    for (const slide of slides) {
      if (slide.getAttribute("variant-image") == null) continue;

      slide.hidden = slide.getAttribute("slide-id") !== selectedImageId;
    }

    slideshow.select({ id: selectedImageId }, undefined, {
      animate: false,
      controlsOverMedia: true,
    });
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll("input[data-variant-id]");
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector("swatches-variant-picker-component");
  }

  get isTouchDevice() {
    if ("matchMedia" in window) {
      return window.matchMedia("(hover: none)").matches;
    }
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
      return true;
    }
    return false;
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== "mouse" || this.isTouchDevice) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, {
        animate: false,
        controlsOverMedia: true,
      });
    } else {
      slideshow.next(undefined, {
        animate: false,
        controlsOverMedia: true,
      });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== "mouse") return;

    const { slideshow } = this.refs;

    if (slideshow.dataset?.activeCarousel === "true") {
      return;
    }

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, {
        animate: false,
        controlsOverMedia: true,
      });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, {
          animate: false,
          controlsOverMedia: true,
        });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute("slide-id");
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, {
        animate: false,
        controlsOverMedia: true,
      });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, {
      animate: false,
      controlsOverMedia: true,
    });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute("data-no-navigation")) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"]',
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute("id");
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest("li");
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set("page", parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }

    const targetLink = event.target.closest("a");
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");
    this.#syncSwatchNames();

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleCardVariantUrlUpdate.bind(this),
    );
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleSwatchVariantUpdate.bind(this),
    );
  }

  #syncSwatchNames() {
    const swatchFieldsets = this.querySelectorAll(
      ".variant-option--swatches[data-swatch-option-position]",
    );

    swatchFieldsets.forEach((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return;

      const swatchName = fieldset.querySelector('[ref="swatchName"]');
      const checkedInput = fieldset.querySelector(
        'input[type="radio"]:checked[data-swatch-name]',
      );

      if (
        !(swatchName instanceof HTMLElement) ||
        !(checkedInput instanceof HTMLInputElement)
      ) {
        return;
      }

      swatchName.textContent =
        checkedInput.dataset.swatchName || checkedInput.value;
    });
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (
      this.pendingVariantId &&
      this.parentProductCard instanceof ProductCard
    ) {
      const currentUrl = new URL(
        this.parentProductCard.refs.productCardLink.href,
      );
      currentUrl.searchParams.set("variant", this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  #handleSwatchVariantUpdate() {
    this.#syncSwatchNames();
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput =
      event.target instanceof HTMLInputElement &&
      event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(
      clickedSwatch.dataset.availableCount || "0",
    );
    const firstAvailableVariantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    const parentProductCard = this.closest("product-card");
    const cardGallery = parentProductCard?.refs?.cardGallery;

    if (cardGallery) {
      cardGallery.setAttribute(
        "data-selected-variant-id",
        clickedSwatch.dataset.variantId,
      );
    }

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);
      this.#syncSwatchNames();

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split("?")[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
    this.#syncSwatchNames();
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    if (!this.closest("product-card")) return;
    window.location = this.closest("product-card").refs.productCardLink.href;

    // const { overflowList } = this.refs;
    // if (overflowList instanceof OverflowList) {
    //   overflowList.showAll();
    // }
  }
}

if (!customElements.get("swatches-variant-picker-component")) {
  customElements.define(
    "swatches-variant-picker-component",
    SwatchesVariantPickerComponent,
  );
}
